import { useCallback, useEffect, useState } from "react";
import { API, rupiah, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const EMPTY = { type: "masuk", category: "", amount: "", description: "", date: new Date().toISOString().slice(0, 10) };

export default function Finance() {
  const [txs, setTxs] = useState(null);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    API.get("/finance/transactions", { params: filter ? { type: filter } : {} }).then((r) => setTxs(r.data)).catch(() => setTxs([]));
    API.get("/finance/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (editId) await API.put(`/finance/transactions/${editId}`, payload);
      else await API.post("/finance/transactions", payload);
      toast.success("Transaksi disimpan");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus transaksi ini?")) return;
    await API.delete(`/finance/transactions/${id}`);
    toast.success("Transaksi dihapus");
    load();
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  return (
    <div className="space-y-6" data-testid="admin-finance">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Pencatatan Keuangan</h1>
          <p className="text-sm text-slate-500">Arus kas masuk &amp; keluar</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} data-testid="add-transaction" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Catat Transaksi
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="fin-masuk">
            <div className="flex items-center justify-between mb-2"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Kas Masuk</p><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
            <p className="text-xl font-bold text-emerald-700">{rupiah(summary.masuk)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="fin-keluar">
            <div className="flex items-center justify-between mb-2"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Kas Keluar</p><TrendingDown className="h-4 w-4 text-red-500" /></div>
            <p className="text-xl font-bold text-red-600">{rupiah(summary.keluar)}</p>
          </div>
          <div className="bg-[#0B1F3A] rounded-md p-5" data-testid="fin-saldo">
            <div className="flex items-center justify-between mb-2"><p className="text-xs font-bold uppercase tracking-widest text-slate-300">Saldo Kas</p><Wallet className="h-4 w-4 text-[#C9A227]" /></div>
            <p className="text-xl font-bold text-[#C9A227]">{rupiah(summary.saldo)}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {[{ v: "", l: "Semua" }, { v: "masuk", l: "Masuk" }, { v: "keluar", l: "Keluar" }].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`fin-filter-${f.v || "all"}`} className={`px-4 py-2 text-xs font-bold rounded-full border transition-colors ${filter === f.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-500 hover:border-[#0B1F3A]"}`}>
            {f.l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="finance-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Tanggal</th>
              <th className="px-5 py-3">Kategori</th>
              <th className="px-5 py-3">Keterangan</th>
              <th className="px-5 py-3">Jumlah</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(txs || []).map((t) => (
              <tr key={t.id} data-testid={`tx-row-${t.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-xs text-slate-500">{t.date}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${t.type === "masuk" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {t.type === "masuk" ? "Masuk" : "Keluar"}
                  </span>{" "}
                  {t.category}
                </td>
                <td className="px-5 py-3 text-slate-600">{t.description || "-"}</td>
                <td className={`px-5 py-3 font-bold ${t.type === "masuk" ? "text-emerald-700" : "text-red-600"}`}>
                  {t.type === "masuk" ? "+" : "-"} {rupiah(t.amount)}
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <button onClick={() => { setForm({ type: t.type, category: t.category, amount: t.amount, description: t.description || "", date: t.date }); setEditId(t.id); }} data-testid={`edit-tx-${t.id}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t.id)} data-testid={`delete-tx-${t.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {txs && txs.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Belum ada transaksi.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6" data-testid="tx-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Transaksi" : "Catat Transaksi"}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {["masuk", "keluar"].map((t) => (
                  <button key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`tx-type-${t}`} className={`p-3 rounded-md border text-sm font-bold transition-colors ${form.type === t ? (t === "masuk" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-red-500 bg-red-50 text-red-700") : "border-slate-300 text-slate-500"}`}>
                    Kas {t === "masuk" ? "Masuk" : "Keluar"}
                  </button>
                ))}
              </div>
              <div>
                <label className={labelCls}>Kategori *</label>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="tx-category" className={inputCls} placeholder="Mis: Penjualan / Pembelian stok / Operasional" />
              </div>
              <div>
                <label className={labelCls}>Jumlah (Rp) *</label>
                <input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-amount" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Tanggal</label>
                <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tx-date" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Keterangan</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tx-desc" className={inputCls} />
              </div>
              <button onClick={save} disabled={!form.category || !form.amount} data-testid="save-tx" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
